"use client";

import { FC } from "react";
import { usePathname } from "next/navigation";
import FooterAccordion from "./FooterAccordion";
import OurCompanies from "./OurCompanies";
import BFLLink from "@repo/shared-ui/BFLLink";
import SectionList from "./SectionList";
import Conditional from "@repo/shared-ui/Conditional";
import BFLImage from "@repo/shared-ui/BFLImage";
import { handleFooterAppDownloadClickDL } from "./FooterAppDownloadFunction";
import { GlobalFooterProps, FooterChild } from "./GlobalFooter.types";
import { useFooterData } from "./useFooterData";

export type { GlobalFooterProps };

const GlobalFooter: FC<GlobalFooterProps> = ({ content }) => {
  const pathname = usePathname() ?? "/";
  const {
    downloadAppText,
    copyRightText,
    corporateLinksTitle,
    yearText,
    companyNameImage,
    corporateSection,
    appDownload,
    corporateLinks,
    sectionTitles,
    loanLinks,
    reachUsData,
    socialMediaIcons,
    calculatorData,
    languagesData,
    importantLinksData,
    productsData,
  } = useFooterData(content, pathname);

  const handleAppDownloadClick = (
    e: React.MouseEvent<HTMLElement>,
    platformUrl: string,
    platformTitle: string
  ) => {
    e.preventDefault();

    // Track analytics before navigation
    handleFooterAppDownloadClickDL(
      "", // loginStatus
      platformUrl, // destination_url
      platformTitle, // platform (Android/iOS)
      platformTitle // cta_text
    );

    // Navigate after analytics completes
    window.location.href = platformUrl;
  };

  return (
    <div className="bg-bfl-blue">
      <FooterAccordion
        data-testid="footer-accordion"
        sectionTitles={sectionTitles}
        languagesData={languagesData}
        loanLinks={loanLinks}
        reachUsData={reachUsData}
        socialMediaIcons={socialMediaIcons}
        calculatorData={calculatorData}
        importantLinksData={importantLinksData}
        productsData={productsData}
      />
      <div className="g-4 flex flex-wrap justify-between p-4 lg:gap-0">
        {corporateSection.map((sectionItem: FooterChild, index: number) => (
          <SectionList
            key={sectionItem?.columnTitle || `corporate-section-${index}`}
            data-testid="section-list"
            data={
              (sectionItem?.children?.find((child: FooterChild) => child.nodeName === "columnList")
                ?.children ?? []) as unknown as { sectionTitle: string; subTitleText?: string }[]
            }
            className="w-[40%] md:w-[18%]"
          />
        ))}
        <OurCompanies
          corporateLinksTitle={corporateLinksTitle ?? ""}
          corporateLinks={corporateLinks}
        />
        <div className="flex w-[100%] flex-col items-center pt-[50px] text-center md:w-[18%] md:pt-0">
          <BFLImage
            className="pt-[20px] pb-[30px]"
            src={companyNameImage ?? ""}
            alt="Bajaj"
            priority={false}
            loading="lazy"
            unoptimized={true}
            width={84}
            height={83.39}
          />
          <div className="mb-2 flex gap-8 text-3xl">
            {appDownload?.map((_icon: FooterChild, index: number) => {
              const platformUrl = _icon?.platformDownloadUrl || "";
              const platformTitle = _icon.platformTitleText || "";

              return (
                <BFLLink
                  key={"appDownload-" + index}
                  href={platformUrl}
                  aria-label={`Download Bajaj Finserv app on ${platformTitle}`}
                  onClick={(e) => handleAppDownloadClick(e, platformUrl, platformTitle)}
                >
                  <>
                    <Conditional if={platformTitle === "Android"}>
                      <i className="icomoon before:content-['\e921']"></i>
                    </Conditional>
                    <Conditional if={platformTitle === "iOS"}>
                      <i className="icomoon before:content-['\e922']"></i>
                    </Conditional>
                  </>
                </BFLLink>
              );
            })}
          </div>
          <h3 data-testid="download-text" className="mb-4 text-left text-sm md:mb-0 md:text-base">
            {downloadAppText}
          </h3>
        </div>
        <h3
          data-testid="copyright-text"
          className="mr-2 flex w-full justify-center text-[12px] md:!justify-end md:pb-0"
        >
          {yearText} {new Date().getFullYear()}
          {copyRightText}
        </h3>
      </div>
    </div>
  );
};
export default GlobalFooter;
